import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import { DEFAULT_ZM_STATUS, ZM_STATUS_FILTER_KEY } from '../constants/dashboard.constants';
import type { DcrDashboardListSearch } from '../types/dashboard.types';
import {
  toDcrDashboardFilterSearch,
  toDcrDashboardFilterValues,
} from '../utils/dcrDashboardFilters';

const route = getRouteApi('/_auth/dashboards/dcr/');

const normalizeFilterValues = (values: Record<string, string>) =>
  toDcrDashboardFilterValues(toDcrDashboardFilterSearch(values));

export function useDcrDashboardFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedSearch = useMemo(
    (): DcrDashboardListSearch => ({
      ...search,
      zmStatus: search.zmStatus ?? DEFAULT_ZM_STATUS,
    }),
    [search],
  );
  const appliedValues = useMemo(() => toDcrDashboardFilterValues(appliedSearch), [appliedSearch]);
  const defaultValues = useMemo(
    () => ({ ...getMonthToDateRange(), [ZM_STATUS_FILTER_KEY]: DEFAULT_ZM_STATUS }),
    [],
  );

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toDcrDashboardFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): DcrDashboardListSearch & { dateFrom: string; dateTo: string } => ({
      ...appliedSearch,
      dateFrom: appliedSearch.dateFrom ?? defaultValues.dateFrom,
      dateTo: appliedSearch.dateTo ?? defaultValues.dateTo,
    }),
    [appliedSearch, defaultValues],
  );

  const zmSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  return { ...filters, ...zmSearch, params, hasAllStatusChoice: true };
}
